import {announce} from '@github-ui/aria-live'
import {PaperAirplaneIcon} from '@primer/octicons-react'
import {IconButton, useMergedRefs} from '@primer/react'
import type {IconButtonProps} from '@primer/react'
import {clsx} from 'clsx'
import {useEffect, useRef} from 'react'
import styles from './ChatInputDisplay.module.css'

export interface ChatInputDisplayProps {
  isDraggedOver?: boolean
  /** Current value of the input */
  value: string
  /** Called when the value changes */
  onChange: (value: string) => void
  /** Called when the user submits (Enter or button click) */
  onSubmit: () => void
  /** Placeholder text */
  placeholder?: string
  /** Whether the input is disabled */
  disabled?: boolean
  /** Whether a submission is in progress */
  isSubmitting?: boolean
  /** Aria label for the textarea */
  ariaLabel?: string
  /** Aria label for the send button */
  sendButtonAriaLabel?: string
  /** Called when user pastes into textarea (for image upload handling) */
  onPaste?: (e: React.ClipboardEvent<HTMLTextAreaElement>) => void
  /** Optional content to render before the send button (e.g., ImageAttachButton) */
  toolbarSlot?: React.ReactNode
  /** Optional content to render on the right side of the toolbar, next to the send button (e.g., ModelPicker) */
  toolbarEndSlot?: React.ReactNode
  /** Optional stop button rendered next to the send button when a session is active */
  stopSlot?: React.ReactNode
  /** Ref to the textarea element */
  ref?: React.Ref<HTMLTextAreaElement>
  /** Whether to allow submission with empty input (default: false - button is inactive when empty) */
  allowEmptySubmit?: boolean
  /** Direction for button tooltips (e.g. 'n' when rendered at the bottom of a side panel) */
  tooltipDirection?: IconButtonProps['tooltipDirection']
  /** Optional content rendered after the textarea (e.g., autocomplete suggestions) */
  afterTextarea?: React.ReactNode
  /** Enables shell-command mode when the value starts with `!`. */
  supportsShellCommands?: boolean
}

export function ChatInputDisplay({
  isDraggedOver = false,
  value,
  onChange,
  onSubmit,
  placeholder = 'Type a message...',
  disabled = false,
  isSubmitting = false,
  ariaLabel,
  sendButtonAriaLabel = 'Send',
  onPaste,
  toolbarSlot,
  toolbarEndSlot,
  stopSlot,
  ref,
  allowEmptySubmit = false,
  tooltipDirection,
  afterTextarea,
  supportsShellCommands = false,
}: ChatInputDisplayProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const pendingSelectionRef = useRef<{start: number; end: number} | null>(null)
  const mergedRef = useMergedRefs(ref, textareaRef)
  const isShellMode = supportsShellCommands && value.startsWith('!')
  const shellCommandValue = isShellMode ? value.slice(1) : value
  const isEmpty = !shellCommandValue.trim()
  const isButtonInactive = disabled || isSubmitting || (!allowEmptySubmit && isEmpty)

  useEffect(() => {
    const pendingSelection = pendingSelectionRef.current
    if (!pendingSelection) return
    pendingSelectionRef.current = null
    textareaRef.current?.setSelectionRange(pendingSelection.start, pendingSelection.end)
  }, [value])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      isShellMode &&
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      e.key === 'Backspace' &&
      !e.nativeEvent.isComposing &&
      e.currentTarget.selectionStart === 0 &&
      e.currentTarget.selectionEnd === 0
    ) {
      e.preventDefault()
      const nextValue = value.slice(1)
      pendingSelectionRef.current = {start: 0, end: 0}
      onChange(nextValue)
      if (!nextValue.startsWith('!')) {
        announce('Shell mode disabled')
      }
      return
    }

    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      if (!disabled && !isSubmitting && (allowEmptySubmit || !isEmpty)) {
        onSubmit()
      }
    }
  }

  const handleSubmitClick = () => {
    if (!disabled && !isSubmitting && (allowEmptySubmit || !isEmpty)) {
      onSubmit()
    }
  }

  const hasToolbar = isShellMode || !!toolbarSlot || !!toolbarEndSlot

  return (
    <div className={styles.container}>
      <div
        className={clsx(
          styles.inputWrapper,
          isDraggedOver && styles.isDraggedOver,
          hasToolbar && styles.inputWrapperWithToolbar,
          isShellMode && styles.shellMode,
        )}
      >
        <textarea
          ref={mergedRef}
          className={clsx(styles.input, hasToolbar && styles.inputWithToolbar)}
          value={value}
          onChange={e => {
            const nextValue = e.target.value
            const nextIsShellMode = supportsShellCommands && nextValue.startsWith('!')
            if (!isShellMode && nextIsShellMode) {
              announce('Shell mode enabled')
            } else if (isShellMode && !nextIsShellMode) {
              announce('Shell mode disabled')
            }
            onChange(nextValue)
          }}
          onKeyDown={handleKeyDown}
          onPaste={onPaste}
          placeholder={isShellMode ? 'Enter shell command' : placeholder}
          disabled={disabled}
          aria-label={isShellMode ? 'Shell command' : (ariaLabel ?? placeholder)}
        />
        {afterTextarea}
        {hasToolbar ? (
          <div className={styles.toolbar}>
            <div className={styles.toolbarGroup}>
              {isShellMode && <span className={styles.shellModeLabel}>Shell</span>}
              {!isShellMode && toolbarSlot}
            </div>
            <div className={styles.toolbarGroup}>
              {toolbarEndSlot}
              <div className={clsx(toolbarEndSlot && styles.submitWithDivider, stopSlot && styles.submitActions)}>
                {stopSlot}
                <IconButton
                  variant="invisible"
                  size="medium"
                  icon={PaperAirplaneIcon}
                  aria-label={sendButtonAriaLabel}
                  tooltipDirection={tooltipDirection}
                  inactive={isButtonInactive}
                  aria-disabled={isButtonInactive}
                  onClick={handleSubmitClick}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className={stopSlot ? styles.sendButtonGroup : undefined}>
            {stopSlot}
            <IconButton
              variant="invisible"
              size="medium"
              icon={PaperAirplaneIcon}
              aria-label={sendButtonAriaLabel}
              tooltipDirection={tooltipDirection}
              inactive={isButtonInactive}
              aria-disabled={isButtonInactive}
              onClick={handleSubmitClick}
              className={stopSlot ? undefined : styles.sendButton}
            />
          </div>
        )}
      </div>
    </div>
  )
}
